/* eslint-disable react/no-unescaped-entities */
import { Box, FormControl, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import Info from "assets/icons/info.svg";
import Upload from "assets/icons/upload.svg";
import { headingColor } from "theme";
import BottomActionButtons from "components/BottomActionButtons/BottomActionButtons";
import { getTextWithoutUnderScore } from "utils/getTextWithoutUnderScore";
import { useAuth } from "hooks/useAuth";
import { AUTH_ROLES } from "constant/authRole";
import useDebouncedCallback from "hooks/useDebounceCallback";
import { useAwsS3 } from "hooks/useAwsS3";
import { AdminAddQuote } from "components/AdminAddQuote";
import { ERROR_MESSAGE } from "constant/projectConstant";

import {
  useStartApplicationByAdminMutation,
  useStartApplicationMutation,
  useUpdateApplicationDataMutation
} from "../../redux/modules/catalog";

const StartApplication = ({
  carriers,
  applicationId,
  industryId,
  riskType,
  applicationData,
  setApplicationData,
  setLoading,
  startApplicationData,
  handleOpen,
  // handleBack,
  applicationAPIData,
  setSnackbarData,
setOpenSnackbar
}) => {
  const [showDocumentType, setShowDocumentType] = useState(false);
  const { data: configData } = useSelector((state) => state.appConfig);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { uploadFile, isLoading: fileIsUploading } = useAwsS3();
  const isApplicationCreated = applicationAPIData?.application?.applicationData?.isDisabled;
  // const isApplicationCreated = searchParams.get("currentScreen");
  const isAdmin = user?.role?.slug === AUTH_ROLES.PORTAL_ADMIN || user?.role?.slug === AUTH_ROLES.PORTAL_SUB_ADMIN;
  const FILES =
    Object.values(configData?.newAppetiteFiles || [])?.map((file) => {
      return {
        name: getTextWithoutUnderScore(file),
        id: file,
        files: [],
        inputRef: React.createRef() // Attach a ref to each file object
      };
    }) || [];

  const [files, setFiles] = useState(FILES);

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  const [apiPayload, setApiPayload] = React.useState(carriers);
  const [agentDetails, setAgentDetails] = useState({
    agentId: "",
    agentName: "",
    agentCompanyId: "",
    agentCompanyName: ""
  });

  useEffect(() => {
    if (!applicationAPIData?.application?.applicationData) return;
  
    const { companyName, files } = applicationAPIData.application.applicationData;
    
  
    // Set company name
    setValue("companyName", companyName);
  
    // Set agent detail
    // Group and process files
    const groupedFiles = files?.reduce((acc, { type, url }) => {
      if (!acc[type]) {
        acc[type] = { type, url: [] };
      }
      acc[type].url.push(url);
      return acc;
    }, {}) || {};
  
    const apiFiles = Object.values(groupedFiles).map(({ type, url }) => ({
      name: getTextWithoutUnderScore(type),
      id: type,
      files: url.map(fileUrl => ({
        name: fileUrl.split('/').pop(),
        url: fileUrl
      }))
    }));
  
    // Set files
    setFiles(apiFiles?.length ? apiFiles : FILES);
  
  }, [applicationAPIData]);

  useEffect(() => {
    if (applicationData) {
      setApiPayload(applicationData);
    }
  }, [applicationData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setShowDocumentType(false); // Set popup to false on outside click
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowDocumentType]);

  const boxRef = useRef(null);
  // const fileRef = useRef([]);

  const [startApplication, { isLoading }] = useStartApplicationMutation();
  const [startApplicationByAdmin, { isLoading: isLoadingByAdmin }] =
    useStartApplicationByAdminMutation();
  const [updateApplication] = useUpdateApplicationDataMutation();

  const handleFileUpload = (event, id) => {
    if (event.target.files && event.target.files.length > 0) {
      // Find the existing file count for this particular id to generate a unique name
      const currentFile = files.find((file) => file.id === id);
      const existingFilesCount = currentFile ? currentFile.files.length : 0;

      const uploadedFiles = Array.from(event.target.files).map(
        (file, fileIndex) => ({
          file,
          type: file.type,
          name: `${id}_file_${existingFilesCount + fileIndex + 1}` // Custom name with id and unique number
        })
      );

      const newFiles = files.map((file) => {
        if (file.id === id) {
          return { ...file, files: [...file.files, ...uploadedFiles] };
        }
        return file;
      });

      setFiles(newFiles);
      setShowDocumentType(false); // Set popup to false on outside click

      // Reset input value to allow re-uploading the same file
      event.target.value = null;
    } 
  };


  const onSubmit = async (data) => {
    if (user) {
      try {
        setLoading(true);
        const payloadData = apiPayload?.availableCarriers?.map((item) => {
          const transformedCarriers = item?.carriers?.map((carrier) => ({
            ...carrier,
            name: carrier.carrierDetails.name,
            catalogCompanyId: carrier.carrierDetails.catalogCompanyId,
            companyServiceId: carrier.carrierDetails.companyServiceId
          }));

          return {
            ...item,
            carriers: transformedCarriers
          };
        });

        const processedData = {
          companyName: data.companyName,
          data: payloadData,
          applicationId,
          industryId,
          ...(riskType ? { riskType } : {}),
          ...(isAdmin
            ? {
                agentId: agentDetails?.agentId,
                agentName: agentDetails?.agentName,
                agentCompanyId: agentDetails?.agentCompanyId,
                agentCompanyName: agentDetails?.agentCompanyName
              }
            : {})
        };

        const allFiles = files
          ?.flatMap((fileGroup) =>
            fileGroup.files.map((file) => ({
              ...file, // include file and type
              type: fileGroup.type,
              id: fileGroup.id
            }))
          )
          .filter((file) => file.file); // filter out any empty file objects

        const fileUploadPromises = allFiles.map((file) =>
          uploadFile(
            file.file,
            `${user?.company?.companyId}/${user?.userId}/${applicationId}/${file.id}`,
            file.name
          )
        );

        const fileUrls = await Promise.all(fileUploadPromises);

        const fileUploads = allFiles.map((fileObj, index) => ({
          type: fileObj.id,
          url: fileUrls[index]
        }));

        const payload = {
          data: { ...processedData, fileUploads },
          token: user.token
        };

        if (isAdmin) {
          await startApplicationByAdmin(payload).then((response) => {
            setLoading(false);
            if (response?.error?.status === 400) {
              setOpenSnackbar(true);
              setSnackbarData({
                severity: "error",
                message:
                  response?.error?.data?.message ||
                  response?.error?.data?.error?.message
              });
            } else {
              const payload = {
                data: {
                  applicationId,
                  data: {
                    ...applicationData,
                    ...processedData,
                    isDisabled:true, 
                    files: fileUploads
                  }
                },

                token: user.token
              };
              updateApplication(payload)
                .then((res) => {
                  if (!res.data?.error) {
                    navigate(
                      `/applications/edit?applicationId=${response?.data?.result?.applicationId}`
                    );
                    setApplicationData({
                      ...applicationData,
                      ...processedData,
                      files: fileUploads
                    });
                    // refetch();
                  }
                })
                .catch((err) => {
                  setSnackbarData({
                    severity: "error",
                    message: err?.data?.error?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG
                  });
                  setOpenSnackbar(true);
                });
            }
          });
        } else {
         try {
          const response = await startApplication(payload).unwrap();
          setLoading(false);
          if(response?.result?.applicationId) {
            const payloadForUpdate = {
              data: {
                applicationId,
                data: {
                  ...applicationData,
                  ...processedData,
                  isDisabled: true, 
                  files: fileUploads
                }
              },
              token: user.token
            };
            
            updateApplication(payloadForUpdate)
            .then((res) => {            
              if (!res.data?.error) {
                navigate(
                  `/applications/edit?applicationId=${response?.result?.applicationId}`
                );
                setApplicationData({
                  ...applicationData,
                  ...processedData,
                  files: fileUploads
                });
                // refetch();
              }
            })
            .catch((err) => {
              setSnackbarData({
                severity: "error",
                message: err?.data?.error?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG
              });
              setOpenSnackbar(true);
            });
          } else {
            setLoading(false);
            setSnackbarData({
              severity: "error",
              message: ERROR_MESSAGE.SOMETHING_WENT_WRONG
            });
            setOpenSnackbar(true);
          }
          
        } catch (error) {
          setLoading(false);          
          // Handle different types of errors
          let errorMessage = ERROR_MESSAGE.SOMETHING_WENT_WRONG;
          
          if (error?.status === 504) {
            errorMessage = ERROR_MESSAGE.REQUEST_TIMED_OUT;
          } else if (error?.status === 400) {
            errorMessage = error?.data?.error?.message || ERROR_MESSAGE.INVALID_REQUEST;
          } else if (error?.status === 401) {
            errorMessage = ERROR_MESSAGE.SESSION_EXPIRED;
          } else if (error?.status === 403) {
            errorMessage = ERROR_MESSAGE.PERMISSION_DENIED;
          } else if (error?.status === 500) {
            errorMessage = ERROR_MESSAGE.SERVER_ERROR;
          }

          setOpenSnackbar(true);
          setSnackbarData({
            severity: "error",
            message: errorMessage
          });
        }
        }
      } catch (err) {
        setLoading(false);
        setSnackbarData({
          severity: "error",
          message: err?.data?.error?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG
        });
        setOpenSnackbar(true);
      }
    } else {
      handleOpen();
    }
  };

  const debouncedOnSubmit = useDebouncedCallback(handleSubmit(onSubmit), 500);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && watch("companyName")) {
      event.preventDefault(); // This can be moved out as it's common to both cases

      // Check if any of the loading states are true, then return early
      if (isLoading || isLoadingByAdmin || fileIsUploading) return;

      // If admin, ensure the agent details are present
      if (isAdmin && (!agentDetails?.agentId || !agentDetails?.agentCompanyId))
        return;

      debouncedOnSubmit();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  const removeFile = (id, fileName) => {
    const updatedFiles = files.map((file) => {
      if (file.id === id) {
        return {
          ...file,
          files: file.files.filter((f) => f.name !== fileName) // Use the custom name property if it exists
        };
      }
      return file;
    });
  
    setFiles(updatedFiles);
  };
  

  const carrierProductElements = startApplicationData?.nonEligibleCarriers.map(
    (item, index) => (
      <span key={index}>
        "<span className="font-[500]">{item.carriers[0].name}</span>" for{" "}
        <span>{item.product.name}</span>
        {index < startApplicationData?.nonEligibleCarriers.length - 1
          ? ", "
          : ""}
      </span>
    )
  );
  return (
    <>
      <div
        ref={boxRef}
        className="mt-4 p-[28px]  bg-white rounded-[8px] w-full"
      >
        {carrierProductElements?.length > 0 && (
          <div className=" px-4 py-3  bg-[#F2F8FF] rounded-[8px] w-full flex gap-[10px]  ">
            <img src={Info} alt="" />
            <div>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#3C5779"
                }}
              >
                Removed ineligible Carriers
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "#3C5779"
                }}
              >
                Please be informed, we have{" "}
                <span className="font-[500]">removed the carriers</span>&nbsp;
                {carrierProductElements} from the quote options, as they are
                currently not eligible.
              </Typography>
            </div>
          </div>
        )}

        {isAdmin && (
          <AdminAddQuote
            agentDetails={agentDetails}
            setAgentDetails={setAgentDetails}
            isDisabled={isApplicationCreated}
            defaultData={applicationAPIData?.application?.applicationData}
          />
        )}

        <FormControl
          fullWidth
          variant="outlined"
          sx={{ mt: "40px", mb: "20px" }}
        >
          <TextField
            size="small"
            error={!!errors.companyName}
            {...register("companyName", { required: true })}
          
            label={"Entity Name"}
            onClick={() => {
              if (!user) {
                handleOpen();
              }
            }}
            disabled={isLoading || isLoadingByAdmin || isApplicationCreated}
            variant="outlined"
            placeholder="ABC LLC"
            InputLabelProps={{ shrink: true }}
          />
        </FormControl>
        <div
          className="p-4 border border-[#E7EBF0] rounded-[8px] "
          
        >
          <div className="flex  justify-between items-center">

          <div>
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 500,
                color: "#3C5779"
              }}
            >
              Upload Documents{" "}
              <span className="text-[#D98C00]">(Optional)</span>
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#5B616B"
              }}
            >
              You can upload Accord, Policy, Loss Run or any other document
            </Typography>

            </div>

            {isApplicationCreated ? null :
          <div className="relative">
            <div
              style={{ border:'1px solid #3487F3' }}
              onClick={() => {
                if (!user) {
                  handleOpen();
                } else {
                setShowDocumentType(!showDocumentType);
              }
              }
              }
              className="flex h-auto items-center cursor-pointer  rounded-[8px] gap-2 px-2 py-1"
            >
              <img src={Upload} className="w-[20px] h-[20px]" alt="" />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#3487F3"
                }}
              >
                Upload File
              </Typography>
            </div>
          </div>}
          </div>

            {files?.length && files?.map((item) => item?.files?.length) ? 
            (
              <Box
              className={`mt-4  max-w-[50vw] overflow-x-scroll w-full h-full ${
                files.some((file) => file.files && file.files.length > 0) ? '' : 'hidden'
              }`}
            >
                {files?.map((item, index) => (
                  <div className="mb-2 flex gap-1" key={item.id + index}>
                    {item?.files?.map((f) => (
                      <Box
                        key={f.name}
                        sx={{
                          border: "1px solid #E1E6ED",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderRadius: ".25rem",
                          bgcolor: "#F9FAFB",
                          px: ".5rem",
                          gap: 1,
                          cursor: isApplicationCreated && "pointer"
                        }}
                        onClick={() => {
                          if (isApplicationCreated) {
                            window.open(f.url, "_blank");
                          }
                        }}
                      >
                        <Typography fontSize=".75rem" color="#9097A1">
                          {f?.name}
                        </Typography>
                        {isApplicationCreated ? null : (
                          <Box
                            sx={{ cursor: "pointer" }}
                             onClick={() => removeFile(item.id, f.name)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="15"
                              viewBox="0 0 14 15"
                              fill="none"
                            >
                              <path
                                d="M7 14.5C5.14354 14.5 3.3629 13.7625 2.05026 12.4497C0.737538 11.1369 0 9.35646 0 7.5C0 5.64354 0.737538 3.8629 2.05026 2.55026C3.36309 1.23754 5.14354 0.5 7 0.5C8.85646 0.5 10.6371 1.23754 11.9497 2.55026C13.2625 3.86309 14 5.64354 14 7.5C13.9978 9.35583 13.2596 11.1352 11.9473 12.4473C10.635 13.7596 8.85564 14.4978 7 14.5ZM9.69946 5.69961V5.69951C9.82219 5.58093 9.89218 5.41805 9.89367 5.24738C9.89516 5.07671 9.82805 4.91255 9.70735 4.79184C9.58664 4.67113 9.42248 4.60402 9.25181 4.60551C9.08114 4.60701 8.91826 4.677 8.79968 4.79973L7 6.60006L5.20032 4.79973C5.03871 4.64365 4.80678 4.58432 4.58999 4.64376C4.3733 4.70321 4.20402 4.87249 4.14457 5.08918C4.08512 5.30597 4.14446 5.5379 4.30054 5.69952L6.10021 7.49985L4.30054 9.30018C4.17781 9.41875 4.10782 9.58163 4.10632 9.75231C4.10483 9.92298 4.17195 10.0871 4.29265 10.2078C4.41335 10.3286 4.57752 10.3957 4.74819 10.3942C4.91886 10.3927 5.08174 10.3227 5.20032 10.2L6.99999 8.39963L8.79967 10.2C8.91825 10.3227 9.08113 10.3927 9.2518 10.3942C9.42247 10.3957 9.58663 10.3285 9.70734 10.2078C9.82805 10.0871 9.89516 9.92298 9.89366 9.75231C9.89217 9.58164 9.82218 9.41876 9.69945 9.30018L7.89978 7.49985L9.69946 5.69961Z"
                                fill="#D4D8DD"
                              />
                            </svg>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </div>
                ))}
              </Box>
            ) : null}
          {/* </div> */}
       
        </div>
        {showDocumentType && (
          <div
            className="absolute top-[264px] right-[80px] bg-white w-[114px] px-3 pb-3 rounded-[4px]"
            style={{ boxShadow: "0px 1px 4px 0px #5B616B3D" }}
          >
            {files.map((item, index) => (
              <Fragment key={`${item.name}-${index}`}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    mt: "12px",
                    cursor: "pointer",
                    color: headingColor
                  }}
                  onClick={() => item.inputRef.current?.click()} // Trigger the input click via ref
                >
                  {item.name}
                </Typography>
                <input
                  multiple
                  accept="image/*,application/pdf"
                  ref={item.inputRef} // Attach the ref here
                  onChange={(event) => handleFileUpload(event, item.id, index)}
                  type="file"
                  style={{ display: "none" }}
                />
              </Fragment>
            ))}
          </div>
        )}
      </div>
      <BottomActionButtons
        handleBack={() => {
          if (isApplicationCreated) {
            navigate(`/start-application?currentScreen=CheckEligibility&applicationId=${applicationId}`, {replace: true});
          } else {
            navigate(
              `/start-application?currentScreen=CheckEligibility&applicationId=${applicationId}`,
              { replace: true }
            );
          }
        }}
        isDisabled={!watch("companyName")}
        handleSubmit={() => {
          if (isApplicationCreated) {
            navigate(`/applications/edit?applicationId=${applicationId}`);
          } else {
            debouncedOnSubmit();
          }
        }}
        backButtonTitle="Back"
        nextButtonTitle="Start Application"
      />
    </>
  );
};

export default StartApplication;
